class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        fitPaymentIconsToSpace(this);
        moveUpsellIntoScrollArea(this);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function fitPaymentIconsToSpace(cartDrawer) {
  const listEl = cartDrawer?.querySelector('.cart-drawer__payment-list');
  if (!listEl) return;

  const items = Array.from(listEl.querySelectorAll('.cart-drawer__payment-item'));
  items.forEach((item) => (item.style.display = ''));

  listEl.style.overflow = 'hidden';
  listEl.style.flexWrap = 'nowrap';

  const computedStyle = getComputedStyle(listEl);
  const gap = parseFloat(computedStyle.columnGap) || parseFloat(computedStyle.gap) || 8;
  const availableWidth = listEl.clientWidth;
  let totalWidth = 0;
  let lastVisibleIndex = -1;

  for (let i = 0; i < items.length; i++) {
    totalWidth += (i > 0 ? gap : 0) + items[i].offsetWidth;
    if (totalWidth <= availableWidth) lastVisibleIndex = i;
  }

  items.forEach((item, i) => {
    item.style.display = i <= lastVisibleIndex ? '' : 'none';
  });
}

function moveUpsellIntoScrollArea(cartDrawer) {
  const scrollArea = cartDrawer?.querySelector('.cart-drawer__scroll-area');
  const footer = cartDrawer?.querySelector('.drawer__footer');
  if (!scrollArea || !footer) return;

  // Reconvert/Upsell.com slots: id="reconvert-slot-cartDrawer-*" or similar - they sit inside drawer__footer with own scroll
  const upsellSlot = footer.querySelector('[id^="reconvert-slot-"], [id^="cartDrawer-slot-"]');
  if (!upsellSlot) return;

  // Only move if not already in scroll area
  if (upsellSlot.closest('.cart-drawer__scroll-area')) return;

  scrollArea.appendChild(upsellSlot);
  upsellSlot.style.overflow = 'visible';
  upsellSlot.style.maxHeight = 'none';

  // Show teaser when upsell is present and set up scroll-based fade
  const teaserEl = cartDrawer.querySelector('.cart-drawer__upsell-teaser');
  if (teaserEl) {
    teaserEl.style.display = '';
    setupUpsellTeaserScrollFade(cartDrawer);
  }
}

function setupUpsellTeaserScrollFade(cartDrawer) {
  const scrollArea = cartDrawer?.querySelector('.cart-drawer__scroll-area');
  const teaserEl = cartDrawer?.querySelector('.cart-drawer__upsell-teaser');
  if (!scrollArea || !teaserEl || teaserEl.style.display === 'none') return;

  const FADE_DISTANCE = 120; // pixels of scroll over which it fades from 1 to 0

  const onScroll = () => {
    const scrollTop = scrollArea.scrollTop;
    const opacity = Math.max(0, 1 - scrollTop / FADE_DISTANCE);
    teaserEl.style.opacity = opacity;
    teaserEl.style.visibility = opacity > 0 ? 'visible' : 'hidden';
  };

  if (scrollArea._teaserScrollHandler) {
    scrollArea.removeEventListener('scroll', scrollArea._teaserScrollHandler);
  }
  scrollArea._teaserScrollHandler = onScroll;
  scrollArea.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

document.addEventListener('DOMContentLoaded', () => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;

  fitPaymentIconsToSpace(cartDrawer);
  moveUpsellIntoScrollArea(cartDrawer);

  const resizeObserver = new ResizeObserver(
    debounce(() => fitPaymentIconsToSpace(cartDrawer), 100)
  );
  const footer = cartDrawer.querySelector('.drawer__footer');
  if (footer) resizeObserver.observe(footer);

  const mutationObserver = new MutationObserver(() => {
    fitPaymentIconsToSpace(cartDrawer);
    moveUpsellIntoScrollArea(cartDrawer);
  });
  const drawerInner = cartDrawer.querySelector('.drawer__inner');
  if (drawerInner) {
    mutationObserver.observe(drawerInner, { childList: true, subtree: true });
  }
});
